import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listMatches } from '../api/client';
import type { AdminMatchListEntry } from '../api/types';
import FlagChips from '../components/scores/FlagChips';

type FilterKey = 'all' | 'proposed' | 'consent_pending' | 'confirmed' | 'rejected';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'proposed', label: 'Proposed' },
  { key: 'consent_pending', label: 'Consent pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'rejected', label: 'Rejected' },
];

function scoreBandClass(score: number): string {
  if (score >= 0.7) return 'text-green-700 bg-green-50';
  if (score >= 0.4) return 'text-amber-700 bg-amber-50';
  return 'text-red-700 bg-red-50';
}

function consentBadge(entry: AdminMatchListEntry) {
  const state = entry.consentState;
  switch (state) {
    case 'consent_pending':
      return <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">consent pending</span>;
    case 'match_confirmed':
      return <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">confirmed</span>;
    case 'match_rejected':
      return <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">rejected</span>;
    default:
      return <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{entry.status}</span>;
  }
}

export default function MatchesPage() {
  const [matches, setMatches] = useState<AdminMatchListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [selectedFlag, setSelectedFlag] = useState<'all' | string>('all');

  useEffect(() => {
    listMatches({ limit: 200 })
      .then((res) => setMatches(res.matches))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Distinct flag ids present in the loaded matches (sorted for stable chips).
  const flagOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const m of matches) {
      for (const f of m.flags ?? []) ids.add(f.flag);
    }
    return ['all', ...[...ids].sort()];
  }, [matches]);

  const filtered = useMemo(() => {
    return matches.filter((m) => {
      if (selectedFlag !== 'all' && !m.flags?.some((f) => f.flag === selectedFlag)) {
        return false;
      }
      switch (filter) {
        case 'proposed':
          return m.status === 'proposed' && m.consentState === 'none';
        case 'consent_pending':
          return m.consentState === 'consent_pending';
        case 'confirmed':
          return m.status === 'confirmed' || m.consentState === 'match_confirmed';
        case 'rejected':
          return m.status === 'rejected' || m.consentState === 'match_rejected';
        case 'all':
        default:
          return true;
      }
    });
  }, [matches, filter, selectedFlag]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading matches...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Matches</h1>
        <span className="text-gray-500">
          {filtered.length} of {matches.length}
        </span>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-pebbl-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Flag filter chips (ids present in the loaded matches) */}
      {flagOptions.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {flagOptions.map((flag) => (
            <button
              key={flag}
              type="button"
              onClick={() => setSelectedFlag(flag)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedFlag === flag
                  ? 'bg-pebbl-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {flag === 'all' ? 'All flags' : flag}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <th className="pb-3 pr-4">Created</th>
              <th className="pb-3 pr-4">Story A</th>
              <th className="pb-3 pr-4">Story B</th>
              <th className="pb-3 pr-4 text-right">Score</th>
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((m) => {
              const finalScore = m.scores.final ?? m.scores.overall;
              return (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">
                    {new Date(m.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-3 pr-4 max-w-[280px]">
                    <Link
                      to={`/stories/${m.storyA.id}`}
                      className="text-pebbl-600 hover:underline block truncate"
                      title={m.storyA.snippet}
                    >
                      {m.storyA.snippet.slice(0, 80)}
                      {m.storyA.snippet.length > 80 ? '...' : ''}
                    </Link>
                    {m.storyA.location && (
                      <span className="text-xs text-gray-400">{m.storyA.location}</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 max-w-[280px]">
                    <Link
                      to={`/stories/${m.storyB.id}`}
                      className="text-pebbl-600 hover:underline block truncate"
                      title={m.storyB.snippet}
                    >
                      {m.storyB.snippet.slice(0, 80)}
                      {m.storyB.snippet.length > 80 ? '...' : ''}
                    </Link>
                    {m.storyB.location && (
                      <span className="text-xs text-gray-400">{m.storyB.location}</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right whitespace-nowrap">
                    <span
                      className={`inline-block px-2 py-0.5 rounded font-medium ${scoreBandClass(
                        finalScore,
                      )}`}
                    >
                      {Math.round(finalScore * 100)}%
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-1 flex-wrap">
                      {consentBadge(m)}
                      <FlagChips flags={m.flags} max={3} />
                    </div>
                  </td>
                  <td className="py-3 text-right">
                    <Link
                      to={`/matches/${m.id}`}
                      className="text-xs text-pebbl-600 hover:underline"
                    >
                      Detail
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No matches found.
          </div>
        )}
      </div>
    </div>
  );
}
